/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { OperationInvoicingDetail } from './OperationInvoicingDetail';
import {
    OperationInvoicingDetailFromJSON,
    OperationInvoicingDetailFromJSONTyped,
    OperationInvoicingDetailToJSON,
    OperationInvoicingDetailToJSONTyped,
} from './OperationInvoicingDetail';
import type { Value } from './Value';
import {
    ValueFromJSON,
    ValueFromJSONTyped,
    ValueToJSON,
    ValueToJSONTyped,
} from './Value';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * An operation is a description of a single element that the provider of a service will invoice. The sales price of a service is the sum of the individual operations that are invoiced. Operations of a service can be invoiced in one invoice or multiple invoices, depending on the contractual rules defined between the customer and the provider. The quantity invoiced in an operation is defined by the provider of the service. The Shared Data Language (SDL) ensures traceability between the requested quantity and the actual quantity in case of any disputes between the customer and the provider.
 * @export
 * @interface Operation
 */
export interface Operation {
    /**
     * Indicates whether the operation is required for the service.
     * @type {boolean}
     * @memberof Operation
     */
    isMandatory?: boolean;
    /**
     * Indicates whether the operation is proposed by default.
     * @type {boolean}
     * @memberof Operation
     */
    isProposedByDefault?: boolean;
    /**
     * Indicates whether the operation is selected for the contractual agreement between the customer and the provider.
     * @type {boolean}
     * @memberof Operation
     */
    isSelected?: boolean;
    /**
     * Free text to describe the operation.
     * @type {string}
     * @memberof Operation
     */
    operationDescription?: string;
    /**
     * Agreed short name given to the operation between the requestor and the provider of the service for the invoicing.
     * @type {string}
     * @memberof Operation
     */
    operationName?: string;
    /**
     * Agreed code given to the operation between the requestor and the provider of the service for the invoicing.
     * @type {string}
     * @memberof Operation
     */
    operationCode?: string;
    /**
     * Subtype is precising the type given by the service provider, for example 'FTL_LTL', 'MILKRUN', 'EXPRESS', 'COURRIER' for 'ROAD_TRANSPORT'... List of values is defined by the service provider.
     * @type {string}
     * @memberof Operation
     */
    operationSubtype?: string;
    /**
     * Type of the operation identified by the service provider, For example 'ROAD_TRANSPORT' or 'FSC' for Fuel surcharge. List of values is defined by the service provider.
     * @type {string}
     * @memberof Operation
     */
    operationType?: string;
    /**
     * Amount of the operation excluding taxes. It is given in the currency of the rate.
     * @type {Value}
     * @memberof Operation
     */
    amountExclTaxes?: Value;
    /**
     * Amount of the operation including all taxes applied to the operation.
     * @type {Value}
     * @memberof Operation
     */
    amountInclTaxes?: Value;
    /**
     * Amount of the operation including all taxes to be prepaid if applicable.
     * @type {Value}
     * @memberof Operation
     */
    requestedPrepaidAmount?: Value;
    /**
     * The actual quantity of the invoicing unit for the operation, validated by the provider. The invoicing unit is defined per agreement between the customer and the provider of the service.
     * @type {Value}
     * @memberof Operation
     */
    actualQuantity?: Value;
    /**
     * List of contacts related to the operation. This can include individuals or teams to be contacted regarding a particular invoiced operation.
     * @type {Array<Contact>}
     * @memberof Operation
     */
    contacts?: Array<Contact>;
    /**
     * List of documents related to an operation, including receipts that justify the operation and any other evidence that may be relevant.
     * @type {Array<Document>}
     * @memberof Operation
     */
    documents?: Array<Document>;
    /**
     * External references of the operation should be used when they are defined in a legacy system that registers the operation under a master data reference identifier. This is useful for searching operations using the ID from the legacy application.
     *                              Frequent ExternalReference Types:
     *                              - 'PROVIDER_ID'
     *                              - 'REQUESTOR_ID' 
     * @type {Array<ExternalReference>}
     * @memberof Operation
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * List of invoice lines associated with the operation. In normal cases, there is only one line, but in case of credit notes or additional invoices, the same line may be processed on several invoices.
     * @type {Array<OperationInvoicingDetail>}
     * @memberof Operation
     */
    operationInvoicingDetails?: Array<OperationInvoicingDetail>;
    /**
     * Quantity of invoicing unit for the operation estimated by the provider. The invoicing unit is defined per agreement between the customer and the provider of the service.
     * @type {Value}
     * @memberof Operation
     */
    plannedQuantity?: Value;
    /**
     * Quantity of invoicing units requested by the customer for the operation. The invoicing unit is defined based on the agreement between the customer and the service provider.
     * @type {Value}
     * @memberof Operation
     */
    requestedQuantity?: Value;
    /**
     * Unit price to be multiplied by the quantity to get the amount of the total price for the operation. It is given in the rate currency defined in the Value. Equals 0 in case of fixed price.
     * @type {Value}
     * @memberof Operation
     */
    unitPrice?: Value;
    /**
     * Special characteristics applied to the operation and to be shared between the service provider and the customer.
     * @type {Array<SpecialCharacteristic>}
     * @memberof Operation
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * N/A
     * @type {Array<SpecialInstruction>}
     * @memberof Operation
     */
    specialInstructions?: Array<SpecialInstruction>;
}

/**
 * Check if a given object implements the Operation interface.
 */
export function instanceOfOperation(value: object): value is Operation {
    return true;
}

export function OperationFromJSON(json: any): Operation {
    return OperationFromJSONTyped(json, false);
}

export function OperationFromJSONTyped(json: any, ignoreDiscriminator: boolean): Operation {
    if (json == null) {
        return json;
    }
    return {
        
        'isMandatory': json['isMandatory'] == null ? undefined : json['isMandatory'],
        'isProposedByDefault': json['isProposedByDefault'] == null ? undefined : json['isProposedByDefault'],
        'isSelected': json['isSelected'] == null ? undefined : json['isSelected'],
        'operationDescription': json['operationDescription'] == null ? undefined : json['operationDescription'],
        'operationName': json['operationName'] == null ? undefined : json['operationName'],
        'operationCode': json['operationCode'] == null ? undefined : json['operationCode'],
        'operationSubtype': json['operationSubtype'] == null ? undefined : json['operationSubtype'],
        'operationType': json['operationType'] == null ? undefined : json['operationType'],
        'amountExclTaxes': json['amountExclTaxes'] == null ? undefined : ValueFromJSON(json['amountExclTaxes']),
        'amountInclTaxes': json['amountInclTaxes'] == null ? undefined : ValueFromJSON(json['amountInclTaxes']),
        'requestedPrepaidAmount': json['requestedPrepaidAmount'] == null ? undefined : ValueFromJSON(json['requestedPrepaidAmount']),
        'actualQuantity': json['actualQuantity'] == null ? undefined : ValueFromJSON(json['actualQuantity']),
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'operationInvoicingDetails': json['operationInvoicingDetails'] == null ? undefined : ((json['operationInvoicingDetails'] as Array<any>).map(OperationInvoicingDetailFromJSON)),
        'plannedQuantity': json['plannedQuantity'] == null ? undefined : ValueFromJSON(json['plannedQuantity']),
        'requestedQuantity': json['requestedQuantity'] == null ? undefined : ValueFromJSON(json['requestedQuantity']),
        'unitPrice': json['unitPrice'] == null ? undefined : ValueFromJSON(json['unitPrice']),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
    };
}

export function OperationToJSON(json: any): Operation {
    return OperationToJSONTyped(json, false);
}

export function OperationToJSONTyped(value?: Operation | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'isMandatory': value['isMandatory'],
        'isProposedByDefault': value['isProposedByDefault'],
        'isSelected': value['isSelected'],
        'operationDescription': value['operationDescription'],
        'operationName': value['operationName'],
        'operationCode': value['operationCode'],
        'operationSubtype': value['operationSubtype'],
        'operationType': value['operationType'],
        'amountExclTaxes': ValueToJSON(value['amountExclTaxes']),
        'amountInclTaxes': ValueToJSON(value['amountInclTaxes']),
        'requestedPrepaidAmount': ValueToJSON(value['requestedPrepaidAmount']),
        'actualQuantity': ValueToJSON(value['actualQuantity']),
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'operationInvoicingDetails': value['operationInvoicingDetails'] == null ? undefined : ((value['operationInvoicingDetails'] as Array<any>).map(OperationInvoicingDetailToJSON)),
        'plannedQuantity': ValueToJSON(value['plannedQuantity']),
        'requestedQuantity': ValueToJSON(value['requestedQuantity']),
        'unitPrice': ValueToJSON(value['unitPrice']),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
    };
}

